import React, { useEffect, useState, useMemo } from "react";
import { Box, Paper, Typography } from "@mui/material";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { type MRT_ColumnDef } from "material-react-table";
import ReusableTable from "../../components/common/Table.tsx";
import Button from "../../components/common/Button.tsx";
import ChatBot from "../../components/common/chatBot/ChatBot.tsx";
import {toast, ToastContainer} from 'react-toastify';
import { handleFetchDatasetView } from "../../modules/datasetModule.ts";
import StatisticTable from "./StatisticsTable.tsx";
import BreadCrumb from "../../components/common/BreadCrumb.tsx";
import { Row } from "reactstrap";

interface FileViewData {
    file: {
        id: number;
        dataset_id: number;
        file_name: string;
        total_records: number;
        total_fields: number;
        created_at: string;
        modified_at: string;
        error_report: string;
    };
    preview?: any[];
}

const DatasetView: React.FC = () => {
    const [datasetTableData, setDatasetTableData] = useState<any[]>([]);
    const [datasetTableHeaders, setDatasetTableHeaders] = useState<string[]>([]);
    const [previewTableData, setPreviewTableData] = useState<any[]>([]);
    const [previewTableHeaders, setPreviewTableHeaders] = useState<string[]>([]);
    const [tableLoading, setTableLoading] = useState<boolean>(true);
    const [tableError, setTableError] = useState<string | null>(null);
    const location = useLocation();
    const isDataSetView = location.pathname.includes('dataset-view')
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    //const toast = useToast();

    useEffect(() => {
        const loadFileData = async () => {
            if (!id) {
                setTableError("No file ID provided");
                setTableLoading(false);
                return;
            }
            const fileId = parseInt(id);
            if (isNaN(fileId)) {
                setTableError("Invalid file ID");
                setTableLoading(false);
                return;
            }

            setTableLoading(true);
            setTableError(null);

            try {
                const data_source = location.state.datasource;
                let data: FileViewData = await handleFetchDatasetView(fileId);

                if (data_source === "datalake") {
                    data = data.data
                    const formattedDataset = [
                        {
                            no_of_records: Number(data.summary.total_records) || 0,
                            no_of_columns: Number(data.summary.total_fields) || 0,
                            dataset_id: data.summary.dataset_id,
                            created_at: new Date(data.summary.created_at).toLocaleDateString(),
                            modified_at: new Date(data.summary.modified_at).toLocaleDateString(),
                            error_report: data.summary.error_report || " ",
                        },
                    ];
                    setDatasetTableHeaders(Object.keys(formattedDataset[0]));
                    setDatasetTableData(formattedDataset);
                } else {
                    const formattedDataset = [
                        {
                            file_id: fileId,
                            file_name: data.file.file_name,
                            no_of_records: Number(data.file.total_records) || 0,
                            no_of_columns: Number(data.file.total_fields) || 0,
                            dataset_id: data.file.dataset_id,
                            created_at: new Date(data.file.created_at).toLocaleDateString(),
                            modified_at: new Date(data.file.modified_at).toLocaleDateString(),
                            error_report: data.file.error_report || " ",
                        },
                    ];

                    setDatasetTableHeaders(Object.keys(formattedDataset[0]));
                    setDatasetTableData(formattedDataset);
                }
                if (data.preview && data.preview[0] === "Failed to load preview") {
                    setPreviewTableHeaders([]);
                    setPreviewTableData([]);
                }
                else if (data.preview && data.preview.length > 0) {
                    setPreviewTableHeaders(Object.keys(data.preview[0]));
                    setPreviewTableData(data.preview);
                } else {
                    setPreviewTableHeaders([]);
                    setPreviewTableData([]);
                }

                setTableLoading(false);
            } catch (error: any) {
                console.error("Error loading file data:", error);
                setTableError(error.message || "Failed to load file data");
                setTableLoading(false);
                toast.error(error.message || "Failed to load file data");
            }
        };

        loadFileData();
    }, [id, toast]);

    const previewColumns = useMemo<MRT_ColumnDef<any>[]>(() => {
        return previewTableHeaders.map((header) => ({
            accessorKey: header,
            header: header
                .split(/[_\s]+/)
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                .join(" "),
            size: 150,
            Cell: ({ cell }) => {
                const value = cell.getValue();
                if (typeof value === "boolean") {
                    return value ? "True" : "False";
                }
                if (value === null || value === undefined) {
                    return "-";
                }
                return value as React.ReactNode;
            },
        }));
    }, [previewTableHeaders]);

    const handleClose = () => {
        // Navigate back to the previous page or file list
        navigate(-1);
    };

    return (
        <>
            <BreadCrumb moduleName={"Dataset"} pageName={"View Dataset"} viewName={location?.state?.datasetName} />
            <ToastContainer position="top-center" />
            {/* <div className="d-flex flex-sm-row flex-column w-100 justify-content-between align-items-lg-center gap-2 mb-1 mt-0">
                <div className="d-flex flex-row gap-2">
                    <h5 className="rv-page-title p-2">Dataset</h5>
                </div>
            </div> */}

            <StatisticTable id={id ?? ""} toast={toast} />

            <h5 className="rv-page-title p-2 mt-3">Data Preview</h5>
            <div className="container-fluid px-0">
                <div className="disable-margin-card table-responsive table-customer border table-border w-100">
                    {previewTableData.length > 0 ? (
                        <ReusableTable
                            columns={previewColumns}
                            data={previewTableData}
                            loading={tableLoading}
                            error={tableError}
                            enablePagination={false}
                            enableSorting={false}
                            enableColumnFilters={false}
                        />
                    ) : (
                        !tableLoading && (
                            <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                                No preview data available
                            </Typography>
                        )
                    )}
                </div>
            </div>
            <Row lg={6} className=" justify-content-end  mt-4">
                <div className="live-preview">
                    <div className="d-flex justify-content-end gap-2 mb-5">
                        <button
                            onClick={handleClose}
                            type="submit"
                            className="btn-label rv-primary-btn rounded-1 px-1 ps-4 py-1 w-md-auto text-nowrap btn btn-secondary"
                        >
                            <i className="ri-close-line label-icon align-middle fs-16 me-1"></i>
                            <span className="px-2 ps-3 ms-2 me-1 fw-bold">
                                Close
                            </span>
                        </button>
                    </div>
                </div>
            </Row>
            {datasetTableData?.[0]?.error_report === " " && <ChatBot datasetTableData={datasetTableData} />}
        </>
    );
};

export default DatasetView;